import { useState } from "react";
import { Plane, Send, Plus, Trash2, ArrowRight, Hotel } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const QUOTE_EMAIL = "info@clubviajemos.com";

const TRIP_TYPES = [
  { id: "oneway", label: "Solo ida" },
  { id: "round", label: "Ida y vuelta" },
  { id: "multi", label: "Multidestino" },
];

const emptySegment = () => ({ from: "", to: "", date: "" });

export const FlightQuoteForm = () => {
  const [tripType, setTripType] = useState("round");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [departDate, setDepartDate] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [passengers, setPassengers] = useState("1");
  const [agentName, setAgentName] = useState("");
  const [segments, setSegments] = useState([emptySegment(), emptySegment()]);
  const [notes, setNotes] = useState("");

  // Vuelo + Hotel
  const [serviceType, setServiceType] = useState("flight");
  const [hotelCity, setHotelCity] = useState("");
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [adults, setAdults] = useState("2");
  const [children, setChildren] = useState("0");
  const [rooms, setRooms] = useState("1");
  const [bedType, setBedType] = useState("");
  const [stars, setStars] = useState("");
  const [budget, setBudget] = useState("");
  const [nearby, setNearby] = useState("");

  const updateSegment = (i, field, value) => {
    setSegments((prev) => prev.map((s, idx) => (idx === i ? { ...s, [field]: value } : s)));
  };
  const addSegment = () => setSegments((prev) => [...prev, emptySegment()]);
  const removeSegment = (i) => setSegments((prev) => prev.filter((_, idx) => idx !== i));

  const buildEmail = () => {
    if (!name || !email || !phone) {
      toast.error("Completa tu nombre, email y teléfono.");
      return;
    }
    if (!country) {
      toast.error("Indica tu país.");
      return;
    }

    const tripLabel = TRIP_TYPES.find((t) => t.id === tripType)?.label || "";
    const serviceLabel = serviceType === "flighthotel" ? "Vuelo + Hotel" : "Solo vuelo";
    const lines = [
      `Hola, soy agente de Club Viajemos y quiero cotizar ${serviceType === "flighthotel" ? "un paquete Vuelo + Hotel" : "un ticket aéreo"}. Mis datos:`,
      "",
      `Nombre: ${name}`,
      `Email: ${email}`,
      `Teléfono: ${phone}`,
      `País: ${country}`,
      `Pasajeros: ${passengers}`,
      `Agente activo: ${agentName || "No indicado"}`,
      `Servicio: ${serviceLabel}`,
      "",
      `Tipo de viaje: ${tripLabel}`,
    ];

    if (tripType === "multi") {
      const valid = segments.filter((s) => s.from || s.to || s.date);
      if (valid.length === 0) {
        toast.error("Agrega al menos un tramo del viaje.");
        return;
      }
      lines.push("Tramos:");
      valid.forEach((s, i) => {
        lines.push(`  ${i + 1}. ${s.from || "?"} → ${s.to || "?"}  ·  Fecha: ${s.date || "por definir"}`);
      });
    } else {
      if (!from || !to || !departDate) {
        toast.error("Completa origen, destino y fecha de ida.");
        return;
      }
      lines.push(`Origen: ${from}`);
      lines.push(`Destino: ${to}`);
      lines.push(`Fecha de ida: ${departDate}`);
      if (tripType === "round") {
        if (!returnDate) {
          toast.error("Completa la fecha de regreso.");
          return;
        }
        lines.push(`Fecha de regreso: ${returnDate}`);
      }
    }

    if (serviceType === "flighthotel") {
      if (!hotelCity) {
        toast.error("Indica la ciudad o zona del hotel.");
        return;
      }
      lines.push("", "— Hotel —");
      lines.push(`Ciudad / zona: ${hotelCity}`);
      lines.push(`Check-in: ${checkIn || "por definir"}  ·  Check-out: ${checkOut || "por definir"}`);
      lines.push(`Habitaciones: ${rooms}  ·  Adultos: ${adults}  ·  Niños: ${children}`);
      if (bedType) lines.push(`Tipo de camas: ${bedType}`);
      if (stars) lines.push(`Categoría: ${stars}`);
      if (budget) lines.push(`Presupuesto: ${budget}`);
      if (nearby) lines.push(`Cercano a / referencia: ${nearby}`);
    }

    if (notes) {
      lines.push("", `Notas: ${notes}`);
    }

    const subject = `Cotización ${serviceLabel} — ${name} (${tripLabel})`;
    const body = lines.join("\n");
    window.location.href = `mailto:${QUOTE_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    toast.success("Abriendo tu correo para enviar la solicitud...");
  };

  const inputCls = "bg-white dark:bg-slate-800";
  const selectCls = "flex h-10 w-full rounded-md border border-input bg-white dark:bg-slate-800 px-3 py-2 text-sm";

  return (
    <div data-testid="flight-quote-form" className="mt-6 bg-white dark:bg-slate-800/60 rounded-xl p-5 sm:p-6 border-2 border-[#45A0EE]/30">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#45A0EE] to-[#1D6FB8] flex items-center justify-center flex-shrink-0">
          <Plane className="h-5 w-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-black text-[#1D1D1B] dark:text-white leading-tight">Cotiza un ticket aéreo con nosotros</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">Envíanos tus datos y el vuelo que quieres — te cotizamos al mejor precio.</p>
        </div>
      </div>

      {/* Recordatorio: cotizar con agente activo */}
      <div data-testid="quote-agent-notice" className="flex items-start gap-2 bg-amber-50 dark:bg-amber-500/10 border border-amber-300/60 dark:border-amber-500/30 rounded-lg px-3 py-2 mb-4">
        <Plane className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs sm:text-sm text-amber-800 dark:text-amber-300 font-medium">
          <strong>Recuerda:</strong> toda cotización debe realizarse siempre con el nombre de un <strong>agente activo</strong> de Club Viajemos. Indícalo abajo para darte la mejor asesoría.
        </p>
      </div>

      {/* Selector de servicio */}
      <div className="flex flex-wrap gap-2 mb-4">
        {[
          { id: "flight", label: "Solo vuelo", icon: Plane },
          { id: "flighthotel", label: "Vuelo + Hotel", icon: Hotel },
        ].map((s) => (
          <button
            key={s.id}
            type="button"
            data-testid={`service-type-${s.id}`}
            onClick={() => setServiceType(s.id)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all border ${
              serviceType === s.id
                ? "bg-[#1D6FB8] text-white border-[#1D6FB8]"
                : "bg-transparent text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:border-[#1D6FB8]"
            }`}
          >
            <s.icon className="h-4 w-4" /> {s.label}
          </button>
        ))}
      </div>

      {/* Tipo de viaje */}
      <div className="flex flex-wrap gap-2 mb-4">
        {TRIP_TYPES.map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`trip-type-${t.id}`}
            onClick={() => setTripType(t.id)}
            className={`px-4 py-2 rounded-full text-sm font-bold transition-all border ${
              tripType === t.id
                ? "bg-[#45A0EE] text-white border-[#45A0EE]"
                : "bg-transparent text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:border-[#45A0EE]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Datos personales */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div>
          <Label className="text-xs">Nombre completo</Label>
          <Input data-testid="quote-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Tu nombre" className={inputCls} />
        </div>
        <div>
          <Label className="text-xs">Tu email</Label>
          <Input data-testid="quote-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tucorreo@email.com" className={inputCls} />
        </div>
        <div>
          <Label className="text-xs">Teléfono</Label>
          <Input data-testid="quote-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 ..." className={inputCls} />
        </div>
        <div>
          <Label className="text-xs">País</Label>
          <Input data-testid="quote-country" value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Tu país" className={inputCls} />
        </div>
        <div className="sm:col-span-2 lg:col-span-4">
          <Label className="text-xs">Nombre del agente activo <span className="text-slate-400">(recomendado)</span></Label>
          <Input data-testid="quote-agent-name" value={agentName} onChange={(e) => setAgentName(e.target.value)} placeholder="Ej: María Pérez / CV-1234" className={inputCls} />
        </div>
      </div>

      {/* Detalle del vuelo */}
      {tripType === "multi" ? (
        <div className="space-y-3 mb-4">
          {segments.map((s, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end bg-slate-50 dark:bg-slate-900/40 rounded-lg p-3">
              <div>
                <Label className="text-xs">Origen</Label>
                <Input data-testid={`seg-from-${i}`} value={s.from} onChange={(e) => updateSegment(i, "from", e.target.value)} placeholder="Ciudad" className={inputCls} />
              </div>
              <div>
                <Label className="text-xs">Destino</Label>
                <Input data-testid={`seg-to-${i}`} value={s.to} onChange={(e) => updateSegment(i, "to", e.target.value)} placeholder="Ciudad" className={inputCls} />
              </div>
              <div>
                <Label className="text-xs">Fecha</Label>
                <Input data-testid={`seg-date-${i}`} type="date" value={s.date} onChange={(e) => updateSegment(i, "date", e.target.value)} className={inputCls} />
              </div>
              {segments.length > 1 && (
                <Button type="button" variant="ghost" size="icon" onClick={() => removeSegment(i)} className="text-rose-500 hover:text-rose-600">
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
          <Button type="button" variant="outline" onClick={addSegment} data-testid="add-segment" className="text-[#45A0EE] border-[#45A0EE]/40">
            <Plus className="h-4 w-4 mr-1" /> Agregar tramo
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <div>
            <Label className="text-xs">Origen</Label>
            <Input data-testid="quote-from" value={from} onChange={(e) => setFrom(e.target.value)} placeholder="Ciudad de salida" className={inputCls} />
          </div>
          <div>
            <Label className="text-xs">Destino</Label>
            <Input data-testid="quote-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="Ciudad de llegada" className={inputCls} />
          </div>
          <div>
            <Label className="text-xs">Fecha de ida</Label>
            <Input data-testid="quote-depart" type="date" value={departDate} onChange={(e) => setDepartDate(e.target.value)} className={inputCls} />
          </div>
          {tripType === "round" && (
            <div>
              <Label className="text-xs">Fecha de regreso</Label>
              <Input data-testid="quote-return" type="date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)} className={inputCls} />
            </div>
          )}
        </div>
      )}

      {/* Datos del hotel */}
      {serviceType === "flighthotel" && (
        <div className="mb-4 rounded-lg border border-[#80C753]/40 bg-[#80C753]/5 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Hotel className="h-4 w-4 text-[#2f7d40]" />
            <h4 className="text-sm font-black text-[#1D1D1B] dark:text-white">Datos del hotel · para buscar el que más se acerque a tu presupuesto</h4>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Ciudad / zona del hotel</Label>
              <Input data-testid="hotel-city" value={hotelCity} onChange={(e) => setHotelCity(e.target.value)} placeholder="Ej: Miami Beach" className={inputCls} />
            </div>
            <div>
              <Label className="text-xs">Check-in</Label>
              <Input data-testid="hotel-checkin" type="date" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} className={inputCls} />
            </div>
            <div>
              <Label className="text-xs">Check-out</Label>
              <Input data-testid="hotel-checkout" type="date" value={checkOut} onChange={(e) => setCheckOut(e.target.value)} className={inputCls} />
            </div>
            <div>
              <Label className="text-xs">N° de habitaciones</Label>
              <Input data-testid="hotel-rooms" type="number" min="1" value={rooms} onChange={(e) => setRooms(e.target.value)} className={inputCls} />
            </div>
            <div>
              <Label className="text-xs">Adultos</Label>
              <Input data-testid="hotel-adults" type="number" min="1" value={adults} onChange={(e) => setAdults(e.target.value)} className={inputCls} />
            </div>
            <div>
              <Label className="text-xs">Niños</Label>
              <Input data-testid="hotel-children" type="number" min="0" value={children} onChange={(e) => setChildren(e.target.value)} className={inputCls} />
            </div>
            <div>
              <Label className="text-xs">Tipo de camas</Label>
              <select data-testid="hotel-beds" value={bedType} onChange={(e) => setBedType(e.target.value)} className={selectCls}>
                <option value="">Sin preferencia</option>
                <option>1 cama</option>
                <option>2 camas</option>
                <option>Cama King</option>
                <option>Camas separadas</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Estrellas (categoría)</Label>
              <select data-testid="hotel-stars" value={stars} onChange={(e) => setStars(e.target.value)} className={selectCls}>
                <option value="">Sin preferencia</option>
                <option>3 estrellas</option>
                <option>4 estrellas</option>
                <option>5 estrellas</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Presupuesto (por noche o total)</Label>
              <Input data-testid="hotel-budget" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="Ej: $120/noche" className={inputCls} />
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <Label className="text-xs">Cercano a algún lugar / referencia</Label>
              <Input data-testid="hotel-nearby" value={nearby} onChange={(e) => setNearby(e.target.value)} placeholder="Ej: cerca de Disney, del centro, de la playa..." className={inputCls} />
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3 mb-4">
        <div>
          <Label className="text-xs">Pasajeros</Label>
          <Input data-testid="quote-passengers" type="number" min="1" value={passengers} onChange={(e) => setPassengers(e.target.value)} className={inputCls} />
        </div>
        <div>
          <Label className="text-xs">Notas adicionales (opcional)</Label>
          <Input data-testid="quote-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Aerolínea preferida, horarios, equipaje..." className={inputCls} />
        </div>
      </div>

      <Button
        type="button"
        onClick={buildEmail}
        data-testid="quote-send-btn"
        className="w-full sm:w-auto bg-gradient-to-r from-[#45A0EE] to-[#1D6FB8] hover:opacity-90 text-white font-bold px-6 py-5 h-auto"
      >
        <Send className="h-4 w-4 mr-2" />
        Enviar solicitud por email
        <ArrowRight className="h-4 w-4 ml-2" />
      </Button>
      <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-2">
        Se abrirá tu aplicación de correo con todos los datos listos para enviarnos a {QUOTE_EMAIL}.
      </p>
    </div>
  );
};

export default FlightQuoteForm;
